import Navbar from "@/components/common/Navbar"
import { PassphraseGenerator } from "@/components/common/passphrase-generator"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Outlet,
  createRootRouteWithContext,
  redirect,
} from "@tanstack/react-router"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Toaster, toast } from "sonner"
import { useAuth } from "../hooks/useAuth"
import { usePassphrase } from "../lib/PassphraseProvider"
import z from "zod"
import { ErrorComponent } from "@/components/ErrorComponent"
import type { RouterContext } from "@/lib/routerContext"
import { useAuthStore } from "@/stores/authStore"
import axios from "axios"

const isProd = import.meta.env.VITE_MODE === "prod"

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,

  beforeLoad: async ({ location }) => {
    if (location.pathname === "/login") {
      return
    }

    const token = localStorage.getItem("access_token")

    if (!token) {
      throw redirect({
        to: "/login",
        search: {
          redirect: location.href,
        },
      })
    }

    useAuthStore.getState().initializeAuth()

    const STORAGE_KEY = "passhprase"
    const existing =
      localStorage.getItem(STORAGE_KEY) ||
      localStorage.getItem("user-passphrase")

    if (!existing) {
      sessionStorage.setItem("redirectAfterPassphrase", location.href)
    }
  },

  validateSearch: z.object({
    search: z.string().optional(),
    tags: z.array(z.string()).optional(),
    sort: z.string().optional(),
    orgs: z.array(z.string()).optional(),
    preset: z.enum(["all", "from_factory", "users"]).optional(),
    types: z.array(z.string()).optional(),
    amplifiers: z.string().optional(),
    target_status: z.string().optional(),
    target_is_template: z.enum(["true", "false"]).optional(),
  }),

  errorComponent: isProd ? ErrorComponent : undefined,
})

function RootComponent() {
  const { isLogged, initializeAuth } = useAuth()
  const { hasPassphrase, setHasPassphrase, externalFlowActive } =
    usePassphrase()

  const [showPassphraseModal, setShowPassphraseModal] = useState(false)
  const passphraseClearedRef = useRef(false)
  const { t } = useTranslation()

  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  useEffect(() => {
    if (
      !hasPassphrase &&
      !showPassphraseModal &&
      !passphraseClearedRef.current &&
      !externalFlowActive
    ) {
      const STORAGE_KEY = "passhprase"
      const existing =
        localStorage.getItem(STORAGE_KEY) ||
        localStorage.getItem("user-passphrase")

      if (!existing) {
        setShowPassphraseModal(true)
      }
    }
  }, [hasPassphrase, showPassphraseModal, externalFlowActive])

  useEffect(() => {
    const savedTheme = localStorage.getItem("custom-theme-json")
    if (savedTheme) return

    axios
      .get("/tweakcn-themes.json")
      .then((resp) => {
        const theme = (resp.data as any[]).find(
          (t) => t.name === import.meta.env.VITE_DEFAULT_THEME,
        )

        if (!theme) return

        const existingStyle = document.getElementById("custom-theme")
        if (existingStyle) existingStyle.remove()

        let cssContent = ""
        const { cssVars, css } = theme

        if (cssVars?.theme) {
          cssContent += ":root {\n"
          Object.entries(cssVars.theme).forEach(([key, value]) => {
            cssContent += `  --${key}: ${value};\n`
          })
          cssContent += "}\n\n"
        }

        if (cssVars?.light) {
          cssContent += ":root.light {\n"
          Object.entries(cssVars.light).forEach(([key, value]) => {
            cssContent += `  --${key}: ${value};\n`
          })
          cssContent += "}\n\n"
        }

        if (cssVars?.dark) {
          cssContent += ":root.dark {\n"
          Object.entries(cssVars.dark).forEach(([key, value]) => {
            cssContent += `  --${key}: ${value};\n`
          })
          cssContent += "}\n\n"
        }

        Object.entries(css || {}).forEach(([layer, selectors]) => {
          cssContent += `@layer ${layer} {\n`

          Object.entries(selectors as Record<string, any>).forEach(
            ([selector, properties]) => {
              cssContent += `  ${selector} {\n`

              Object.entries(properties as Record<string, any>).forEach(
                ([property, value]) => {
                  const cssProperty = property
                    .replace(/([A-Z])/g, "-$1")
                    .toLowerCase()

                  cssContent += `    ${cssProperty}: ${value};\n`
                },
              )

              cssContent += "  }\n"
            },
          )

          cssContent += "}\n\n"
        })

        if (cssContent.trim()) {
          const style = document.createElement("style")
          style.id = "custom-theme"
          style.textContent = cssContent
          document.head.appendChild(style)
        }
      })
      .catch(() => {
        // silently fail
      })
  }, [])

  const handlePassphraseSaved = () => {
    setHasPassphrase(true)
    passphraseClearedRef.current = false

    const redirectTo = sessionStorage.getItem("redirectAfterPassphrase")

    if (redirectTo) {
      sessionStorage.removeItem("redirectAfterPassphrase")
      window.location.href = redirectTo
      return
    }

    setShowPassphraseModal(false)
  }

  const handlePassphraseCleared = () => {
    setHasPassphrase(false)
    passphraseClearedRef.current = true

    toast.warning(t("passphrase_cleared_title"), {
      description: t("passphrase_cleared_desc"),
    })
  }

  const handlePassphraseDialogChange = (newOpen: boolean) => {
    if (!newOpen && !hasPassphrase) {
      const STORAGE_KEY = "passhprase"
      const currentPassphrase =
        localStorage.getItem(STORAGE_KEY) ||
        localStorage.getItem("user-passphrase")

      if (!currentPassphrase) {
        return
      }
    }

    setShowPassphraseModal(newOpen)
  }

  return (
    <div className="flex min-h-screen flex-col">
      {isLogged() && <Navbar />}

      <div className="grow overflow-x-hidden">
        <Toaster />
        <Outlet />
      </div>

      <Dialog
        open={showPassphraseModal}
        onOpenChange={handlePassphraseDialogChange}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>🔐 {t("set_passphrase_required_title")}</span>
            </DialogTitle>
          </DialogHeader>

          <PassphraseGenerator
            onPassphraseSaved={handlePassphraseSaved}
            onPassphraseCleared={handlePassphraseCleared}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}
